import React from 'react'
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import update from 'immutability-helper';
import { useAPI } from 'contexts/APIProvider'
import { useMixpanel } from 'contexts/MixpanelProvider'
import { PracticeProfileForm, WithContactStrings } from 'types/interfaces';
import Header from 'components/Header'
import FormField from 'components/FormField'
import CQCLocationSelector from 'components/CQCLocationSelector'
import CQCLocation, { CQC_PREFILLED_FIELDS } from 'logic/CQCLocation'
import Paper from '@mui/material/Paper'
import ErrorList from 'components/ErrorList'
import Loading from 'components/Loading'
import { isEmpty } from 'lodash';
import PracticeSignupContext from './context';
import PracticeProfileError from 'errors/PracticeProfileError';
import { MenuItem } from '@mui/material';
import { MARKETING_CHANNELS, MULTIPLE_EMAILS_HELPER_TEXT, MULTIPLE_PHONES_HELPER_TEXT } from 'types/constants';
import { toContactLists } from 'logic/helpers';

const PracticeSignupBasicInfoEdit: React.FC = () => {
  const { toNextStep, practice, trackOnce } = React.useContext(PracticeSignupContext)
  const { track } = useMixpanel()
  const { api } = useAPI()
  const [form, setForm] = React.useState<WithContactStrings<PracticeProfileForm>>({});
  const [error, setError] = React.useState<PracticeProfileError>();
  const [loading, setLoading] = React.useState<boolean>(false)
  const [cqcLocation, setCqcLocation] = React.useState<CQCLocation | null>(null)

  const selectCqcLocation = React.useCallback((location: CQCLocation | null) => {
    setCqcLocation(location)
    setForm((form) => update(form, location ? { $merge: location.toPracticeForm() } : { $unset: CQC_PREFILLED_FIELDS }))
    setError((error) => error && update(error, { cqc_location_id: { $set: [] } }))
  }, [])

  function updateForm(name: string, value: any) {
    setForm(update(form, { [name]: { $set: value } }))
    if (error) setError(update(error, { [name]: { $set: [] } }))
  }

  async function save() {
    if (!practice) return
    try {
      setLoading(true)
      await api.editPractice(toContactLists(form), practice.practice_id)
      track('practice_signup_v1_basic_info_edited')
      toNextStep()
    } catch (e: any) {
      if (e instanceof PracticeProfileError) {
        setError(e)
      } else {
        console.error('unhandled exception', e)
      }
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    trackOnce('practice_signup_v1_basic_info_step_reached')
  }, [trackOnce])

  React.useEffect(() => {
    if (!practice) return
    setForm((form) => update(form, {
      'name': { $set: practice.practice_name },
      'cqc_location_id': { $set: practice.cqc_location_id },
      'address': { $set: practice.address },
      'latitude': { $set: practice.latitude },
      'longitude': { $set: practice.longitude },
      'line1': { $set: practice.line1 },
      'line2': { $set: practice.line2 },
      'city': { $set: practice.city },
      'county': { $set: practice.county },
      'country': { $set: practice.country },
      'postcode': { $set: practice.postcode },
      'email_for_inquiries': { $set: practice.email_for_inquiries },
      'email_for_notifications': { $set: practice.email_for_notifications },
      'phone_for_inquiries': { $set: practice.phone_for_inquiries },
      'phone_for_notifications': { $set: practice.phone_for_notifications },
      'marketing_channel': { $set: practice.marketing_channel },
      'parent_company': { $set: practice.parent_company },
    }))
    setCqcLocation(practice.cqc_location)
  }, [practice])

  if (isEmpty(form)) return <Loading />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <Header variant='h3' text='Basic Information'></Header>
      <CQCLocationSelector
        label='Enter your practice postcode to find your practice (CQC registered)'
        value={cqcLocation}
        onChange={selectCqcLocation}
        errors={error?.cqc_location_id}
      />
      {error?.cqc_prefilled_errors.length ? (
        <Alert severity='error'><ErrorList errors={error.cqc_prefilled_errors} /></Alert>
      ) : null}
      {practice && !cqcLocation ? (
        <Paper sx={{ whiteSpace: 'pre-line', p: 1 }}>
          {[practice.practice_name, practice.address].filter(Boolean).join('\n')}
        </Paper>
      ) : null}
      <FormField
        name='parent_company'
        label='Practice Group Name'
        helperText='If you are part of a group of practices, enter the name of the group here. If you are independent, leave this blank.'
        onChange={(e) => updateForm('parent_company', e.target.value)}
        value={form.parent_company}
        errors={error?.parent_company}
      />
      <FormField
        name='phone_for_inquiries'
        label='Phone Number (General Inquiries)'
        onChange={(e) => updateForm('phone_for_inquiries', e.target.value)}
        value={form.phone_for_inquiries}
        errors={error?.phone_for_inquiries}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='phone_for_notifications'
        label='Phone Number (to receive SMS notifications)'
        onChange={(e) => updateForm('phone_for_notifications', e.target.value)}
        value={form.phone_for_notifications}
        errors={error?.phone_for_notifications}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='email_for_inquiries'
        label='Email (General Inquiries)'
        onChange={(e) => updateForm('email_for_inquiries', e.target.value)}
        value={form.email_for_inquiries}
        errors={error?.email_for_inquiries}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      <FormField
        name='email_for_notifications'
        label='Email (to receive notifications)'
        onChange={(e) => updateForm('email_for_notifications', e.target.value)}
        value={form.email_for_notifications}
        errors={error?.email_for_notifications}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      <FormField
        select
        name='marketing_channel'
        label='How did you hear about Locumloop?'
        onChange={(e) => updateForm('marketing_channel', e.target.value)}
        value={form.marketing_channel ?? ''}
        errors={error?.marketing_channel}
      >
        <MenuItem value=''>Please select</MenuItem>
        {MARKETING_CHANNELS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </FormField>
      {error?.schema ? (
        <Alert severity='error'><ErrorList errors={error.schema} /></Alert>
      ) : null}
      <Button variant="contained" color="primary" onClick={save} disabled={loading}>
        {loading ? 'Please Wait' : 'Next'}
      </Button>
    </Box>
  )
}

export default PracticeSignupBasicInfoEdit